import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import DeliveryDiningRounded from '@mui/icons-material/DeliveryDiningRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import ImportExportRounded from '@mui/icons-material/ImportExportRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import SpaceDashboardRounded from '@mui/icons-material/SpaceDashboardRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import type { Schemas } from '../api/client';
import type { Me } from '../auth/AuthProvider';

export type ShopMenuKey = Schemas['ShopMenuKey'];

export interface NavItem {
  /** The menu's key: the platform admin can turn it off per shop (GET /me hiddenMenus). */
  key: ShopMenuKey;
  label: string;
  path: string;
  icon: SvgIconComponent;
  /** Any one of these permissions shows the item (the backend enforces access); [] = everyone. */
  anyOf: string[];
  /** Milestone that builds the screen; shown as "coming soon" until then. */
  milestone?: string;
  keywords?: string;
  /** Shown only while this shop setting is on (e.g. the optional point of sale). */
  requires?: keyof NavFlags;
  /** Shown even while `requires` is off to holders of any of these (they can switch it on). */
  alwaysFor?: string[];
}

/** Shop switches that show or hide optional modules in the navigation. */
export interface NavFlags {
  posEnabled?: boolean;
  /** The Foodpanda add-on is enabled for the shop (the shop switch is managed on its page). */
  foodpandaLicensed?: boolean;
  /** Menus the platform admin turned off for this shop: never shown, whatever the permissions. */
  hiddenMenus?: readonly ShopMenuKey[];
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/',
        icon: SpaceDashboardRounded,
        anyOf: ['dashboard.view'],
      },
      {
        key: 'fast_sale',
        label: 'Fast sale',
        path: '/sales/counter',
        icon: PointOfSaleRounded,
        anyOf: ['sales.create', 'sales.invoice'],
        milestone: 'W5',
        keywords: 'new sale counter checkout',
      },
      {
        key: 'pos',
        label: 'Point of Sale',
        path: '/pos',
        icon: ShoppingCartCheckoutRounded,
        anyOf: ['pos.use', 'pos.manage'],
        milestone: 'W10',
        keywords: 'pos register till cashier session',
        requires: 'posEnabled',
        alwaysFor: ['pos.manage'],
      },
    ],
  },
  {
    title: 'Sales',
    items: [
      {
        key: 'quotations',
        label: 'Quotations',
        path: '/quotations',
        icon: RequestQuoteRounded,
        anyOf: ['quotations.view'],
        milestone: 'W4',
      },
      {
        key: 'sales_orders',
        label: 'Orders & deliveries',
        path: '/sales/orders',
        icon: LocalShippingRounded,
        anyOf: ['sales.view'],
        milestone: 'W5',
      },
      {
        key: 'invoices',
        label: 'Invoices',
        path: '/sales/invoices',
        icon: ReceiptLongRounded,
        anyOf: ['sales.view'],
        milestone: 'W5',
      },
      {
        key: 'foodpanda',
        label: 'Foodpanda',
        path: '/foodpanda',
        icon: DeliveryDiningRounded,
        anyOf: ['foodpanda.orders', 'foodpanda.manage'],
        keywords: 'food delivery online orders rider kitchen',
        requires: 'foodpandaLicensed',
      },
      {
        key: 'returns',
        label: 'Returns & notes',
        path: '/returns',
        icon: AssignmentReturnRounded,
        anyOf: ['sales.view', 'purchases.view'],
        milestone: 'W7',
      },
    ],
  },
  {
    title: 'Purchasing',
    items: [
      {
        key: 'purchases',
        label: 'Purchases',
        path: '/purchases',
        icon: ShoppingBagRounded,
        anyOf: ['purchases.view'],
        milestone: 'W6',
        keywords: 'po receiving bills',
      },
    ],
  },
  {
    title: 'Payments',
    items: [
      {
        key: 'payments',
        label: 'Payments',
        path: '/payments',
        icon: PaymentsRounded,
        anyOf: ['payments.view'],
        milestone: 'W7',
        keywords: 'receipts refunds',
      },
    ],
  },
  {
    title: 'Catalog & stock',
    items: [
      {
        key: 'products',
        label: 'Products',
        path: '/products',
        icon: CategoryRounded,
        anyOf: ['products.view'],
        milestone: 'W3',
        keywords: 'items sku barcode',
      },
      {
        key: 'bom',
        label: 'Bills of materials',
        path: '/bom',
        icon: AccountTreeRounded,
        anyOf: ['products.view'],
        milestone: 'W3',
        keywords: 'bom bill of materials components recipe kit',
      },
      {
        key: 'inventory',
        label: 'Inventory',
        path: '/inventory',
        icon: Inventory2Rounded,
        anyOf: ['inventory.view'],
        milestone: 'W4',
        keywords: 'stock counts adjustments',
      },
    ],
  },
  {
    title: 'People',
    items: [
      {
        key: 'customers',
        label: 'Customers',
        path: '/customers',
        icon: PeopleAltRounded,
        anyOf: ['customers.view'],
        milestone: 'W3',
      },
      {
        key: 'suppliers',
        label: 'Suppliers',
        path: '/suppliers',
        icon: StorefrontRounded,
        anyOf: ['suppliers.view'],
        milestone: 'W3',
      },
    ],
  },
  {
    title: 'Insights',
    items: [
      {
        key: 'reports',
        label: 'Reports',
        path: '/reports',
        icon: InsightsRounded,
        anyOf: ['reports.view'],
        milestone: 'W8',
      },
      {
        key: 'accounting',
        label: 'Accounting',
        path: '/accounting',
        icon: AccountBalanceRounded,
        anyOf: ['ledger.view', 'journal.view'],
        milestone: 'W8',
        keywords: 'ledger journal trial balance',
      },
      {
        key: 'reminders',
        label: 'Reminders',
        path: '/reminders',
        icon: NotificationsActiveRounded,
        anyOf: ['reminders.view'],
        milestone: 'W8',
      },
      {
        key: 'audit',
        label: 'Activity log',
        path: '/audit',
        icon: HistoryRounded,
        anyOf: ['audit.view'],
        milestone: 'W8',
      },
    ],
  },
  {
    title: 'Shop',
    items: [
      {
        key: 'staff',
        label: 'Staff & roles',
        path: '/staff',
        icon: BadgeRounded,
        anyOf: ['staff.view', 'roles.manage'],
        milestone: 'W2',
      },
      {
        key: 'sync',
        label: 'Devices & sync',
        path: '/sync',
        icon: SyncRounded,
        anyOf: ['sync.view', 'devices.manage'],
        milestone: 'W8',
      },
      {
        key: 'subscription',
        label: 'Subscription',
        path: '/subscription',
        icon: WorkspacePremiumRounded,
        anyOf: ['subscription.view'],
        milestone: 'W2',
      },
      {
        key: 'data',
        label: 'Import & export',
        path: '/data',
        icon: ImportExportRounded,
        anyOf: ['data.import', 'data.export'],
        keywords: 'excel csv spreadsheet upload download opening balances trial balance',
      },
      {
        key: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: SettingsRounded,
        anyOf: ['settings.view'],
        milestone: 'W2',
      },
      {
        key: 'support',
        label: 'Support',
        path: '/support',
        icon: SupportAgentRounded,
        anyOf: [],
        keywords: 'help chat message contact packbytes',
      },
    ],
  },
];

export function visibleNav(can: (key: string) => boolean, flags: NavFlags = {}): NavGroup[] {
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter(
      (i) =>
        !flags.hiddenMenus?.includes(i.key) &&
        (i.anyOf.length === 0 || i.anyOf.some((k) => can(k))) &&
        (!i.requires || flags[i.requires] === true || (i.alwaysFor ?? []).some((k) => can(k))),
    ),
  })).filter((g) => g.items.length > 0);
}

/** Full-screen modules that live outside the app shell (their own route in App.tsx). */
export const FULL_SCREEN_PATHS = new Set(['/pos']);

/** The menu a path belongs to (the longest matching menu path; '/' only for the dashboard itself). */
export function menuOfPath(pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  for (const item of NAV.flatMap((g) => g.items)) {
    const match =
      item.path === '/'
        ? pathname === '/'
        : pathname === item.path || pathname.startsWith(`${item.path}/`);
    if (match && (!best || item.path.length > best.path.length)) best = item;
  }
  return best;
}

/**
 * Whether PackBytes turned this menu off for the shop. A /me kept from before menus existed (an
 * offline desktop) has no list: every menu is on.
 */
export function isMenuOff(me: Pick<Me, 'hiddenMenus'> | null, key: ShopMenuKey) {
  return (me?.hiddenMenus ?? []).includes(key);
}
